<script setup lang="ts">
import { hide as hideIntercom } from '@intercom/messenger-js-sdk'

import MainContainer from '~/components/shared/containers/main.vue'
import { BtnOutlined } from '~/components/ui/Buttons/'

import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useGetMediaUrl } from '~/composables/useGetMediaUrl'

import { useBandSignupStore } from '~/stores/bandSignup'
import { useDraftStore } from '~/stores/draft'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useMiscSendtrackFiltersStore } from '~/stores/miscSendtrackFilters'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import { trackEvent } from '~/helpers/LegacyTrackEvent'

import { Breakpoints } from '~/enums/breakpoints'
import { FilterCategory } from '~/enums/FilterCategory'

import type { InfluencerListingNudgeKind } from '~/types/influencerListingNudgeKind'

type Props = {
  kind: InfluencerListingNudgeKind
  hidePicture?: boolean
  titleProps?: Record<string, string>
}

const props = withDefaults(defineProps<Props>(), {
  kind: 'band_budget',
  hidePicture: false,
  titleProps: () => ({}),
})

const { getMediaUrl } = useGetMediaUrl()
const { t, te } = useI18n()
const segmentTrack = useSegmentTrack()
const route = useRoute()

const { id: DRAFT_ID, influencers: CURATORS_SELECTED } =
  storeToRefs(useDraftStore())
const { id: BAND_ID } = storeToRefs(useUserBandStore())
const { IS_INF, id: USER_ID } = storeToRefs(useUserStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { SET_DISPLAY: SIGNUP_SET_DISPLAY } = useBandSignupStore()
const { BACKUP_FILTER_SELECTION, SET_DISPLAY_FILTERS, SET_EXPANDED_MENU } =
  useMiscSendtrackFiltersStore()

const i18nPreposition = computed(() => {
  return `draft.selection_help_card.${props.kind}` as `draft.selection_help_card.${InfluencerListingNudgeKind}`
})

const title = computed(() => {
  return te(`${i18nPreposition.value}.title`)
    ? t(`${i18nPreposition.value}.title`, props.titleProps)
    : ''
})

const subtitle = computed(() => {
  if (!te(`${i18nPreposition.value}.subtitle`)) return ''

  return t(`${i18nPreposition.value}.subtitle`)
})

const picture = computed(() => {
  const pictureName = te(`${i18nPreposition.value}.picture`)
    ? t(`${i18nPreposition.value}.picture`)
    : props.kind.replace('_ab', '')
  return getMediaUrl(`${pictureName}.svg`)
})

const btnText = computed(() => {
  if (!te(`${i18nPreposition.value}.btn`)) return ''

  return t(`${i18nPreposition.value}.btn`)
})

const btnAction = computed(() => {
  if (!te(`draft.selection_help_card.${props.kind}.btn_action`)) return ''

  return t(`draft.selection_help_card.${props.kind}.btn_action`)
})

const hasBtnAction = computed(() => {
  return !btnAction.value.startsWith(i18nPreposition.value)
})

const doesUserHaveMultiplePillsExperience = computed(() => {
  return !(SCREEN_WIDTH.value < Breakpoints.lg)
})

function openBandSignup(): void {
  trackEvent(
    {
      category: 'Band Signup',
      action: 'Open signup dialog',
      label: 'Seo Listing Nudge',
    },
    route,
  )
  SIGNUP_SET_DISPLAY(true)
}

function openBudgetLink(): void {
  window.open(t('draft.selection_help_card.band_budget.link') as string)
  trackEvent(
    {
      category: 'External visit',
      action: 'Open help article',
      link: t('draft.selection_help_card.band_budget.link'),
      origin: 'Sendtrack / Budget nudge',
    },
    route,
  )

  segmentTrack('Catalog - Budget FAQ Article Clicked', {
    user_id: USER_ID.value,
    id_draft: DRAFT_ID.value,
  })
}

function openSpecificFilters(filterCategory: FilterCategory): void {
  if (doesUserHaveMultiplePillsExperience.value)
    SET_EXPANDED_MENU(filterCategory)
  else SET_DISPLAY_FILTERS(true)

  BACKUP_FILTER_SELECTION()
  const details = {
    id_band: BAND_ID.value,
    num_curators_selected: CURATORS_SELECTED.value.length,
  }
  trackEvent(
    {
      category: 'Catalog',
      action: 'Genre Filters Nudge Clicked',
      details,
    },
    route,
  )

  segmentTrack(
    filterCategory === FilterCategory.Genres
      ? 'Catalog - Genre Filters Nudge Clicked'
      : 'Catalog - Advanced Filters Nudge Clicked',
    details,
  )
}

function handleBtnClick($event: Event) {
  if (btnAction.value === 'open_intercom') hideIntercom()
  else if (btnAction.value === 'open_budgetLink') openBudgetLink()
  else if (btnAction.value === 'open_signup') openBandSignup()
  else if (btnAction.value === 'filter_by_genre')
    openSpecificFilters(FilterCategory.Genres)
  else if (btnAction.value === 'filter_advanced_filters')
    openSpecificFilters(FilterCategory.Advanced)
}
</script>

<template>
  <MainContainer
    class="helpCardWrapper alwaysShadow"
    :class="{ small: IS_INF }"
  >
    <div class="contentWrapper tw-h-full">
      <div>
        <span
          v-if="title"
          class="tw-text-body-lg tw-mb-sm tw-inline-block tw-font-bold"
        >
          {{ title }}
        </span>
        <div v-if="subtitle" class="tw-text-body tw-mb-sm tw-block">
          {{ subtitle }}
        </div>
      </div>

      <!-- When we don't have a subtitle, we add an empty subtitle: '' property to the json -->
      <div class="tw-w-full">
        <img
          v-if="!hidePicture"
          class="tw-block tw-max-h-[140px] tw-px-sm"
          :class="{ 'tw-mb-sm': kind === 'band_budget' }"
          :src="picture"
          loading="lazy"
        />

        <BtnOutlined
          v-if="hasBtnAction && btnText"
          small
          class="!tw-w-full tw-overflow-hidden"
          :class="{ 'tw-mt-auto': hidePicture }"
          @click.stop="handleBtnClick"
        >
          {{ btnText }}
        </BtnOutlined>
      </div>
    </div>
  </MainContainer>
</template>

<style scoped lang="scss">
.helpCardWrapper {
  @apply tw-bg-white;
}

.subtitle {
  @apply tw-h-[76px] tw-font-sans tw-font-normal tw-tracking-normal;
  font-size: var(--font-size-big);
}

.helpCardWrapper .subtitle {
  @apply tw-mb-2 tw-h-auto;
}

.contentWrapper {
  @apply tw-relative tw-flex tw-flex-col tw-items-start tw-justify-between;
}

.small .contentWrapper {
  @apply tw-max-h-[256px];
}

.title {
  @apply tw-mb-2 tw-font-sans tw-text-base tw-font-medium;
}

img {
  @apply tw-mx-auto tw-max-w-full tw-overflow-hidden tw-object-contain tw-object-center;
}

.btn.main.filled {
  @apply tw-w-full;
}

@media screen and (min-width: 480px) {
  .title {
    @apply tw-text-xl;
  }
}

.helpCardWrapper :deep(.underWrap) {
  @apply tw-h-full tw-bg-orange-100;
}
</style>
